// Utility to create and persist a stable per-tab ID across refreshes

const TAB_ID_STORAGE_KEY = 'tabId';

let inMemoryTabId: string | null = null;

function generateTabId(): string {
  // Prefer cryptographically strong UUID when available
  try {
    if (typeof crypto !== 'undefined') {
      if (typeof (crypto as any).randomUUID === 'function') {
        return (crypto as any).randomUUID();
      }
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // Convert to hex
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    // ignore and fall back
  }

  // Fallback: time-based with random
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getOrCreateTabId(): string {
  // If we already generated in-memory (e.g., storage blocked), reuse it
  if (inMemoryTabId) return inMemoryTabId;

  try {
    if (typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined') {
      const existing = window.sessionStorage.getItem(TAB_ID_STORAGE_KEY);
      if (existing && existing.length > 0) {
        inMemoryTabId = existing;
        return existing;
      }

      const newId = generateTabId();
      window.sessionStorage.setItem(TAB_ID_STORAGE_KEY, newId);
      inMemoryTabId = newId;
      return newId;
    }
  } catch {
    // sessionStorage may be unavailable (privacy mode, denied access). Fall back to memory.
  }

  inMemoryTabId = generateTabId();
  return inMemoryTabId;
}

// Eagerly compute a singleton tab ID for convenient imports
export const TAB_ID = getOrCreateTabId();

export { TAB_ID_STORAGE_KEY };
